import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';
import React, { Suspense } from 'react';
import ErrorBoundary from 'error/components/ErrorBoundary';
import MetadataRouter from 'components/MetadataRouter';
import ApolloProvider from 'contexts/ApolloContext/ApolloContext';
import ExecutionEnvironmentProvider from 'contexts/ExecutionEnvironmentContext/ExecutionEnvironmentContext';
import CreatorSessionProvider from 'contexts/CreatorSessionContext/CreatorSessionContext';
import SweetenerUpsellModalProvider from 'modals/SweetenerUpsellModal/SweetenerUpsellModalContext';
import { AuthenticationProvider } from 'contexts/AuthenticationProvider/AuthenticationProvider';
import ActivePlayerProvider from 'contexts/ActivePlayerContext/ActivePlayerContext';
import ReactPlayerProvider from 'contexts/ReactPlayerContext/ReactPlayerContext';
import OnboardingProvider from 'contexts/OnboardingContext/OnboardingContext';
import NotificationsProvider from 'contexts/NotificationsContext/NotificationsContext';
import SelectedSamplesProvider from 'contexts/SelectedSamplesContext/SelectedSamplesContext';
import CreatorContextProvider from 'controllers/business/Creator/Creator.controller';
import ClientUpdateProvider from 'contexts/ClientUpdateContext/ClientUpdateContext';
import ModalProvider from 'contexts/ModalContext/ModalContext';
import SessionStorageProvider from 'contexts/SessionStorageContext/SessionStorageContext';
import PurchaseControllerProvider from 'contexts/PurchaseControllerContext/PurchaseControllerContext';
import UserCreditsProvider from 'contexts/UserCreditsContext/UserCreditsContext';
import PricingProviderWithToken from 'components/PricingProviderWithToken/PricingProviderWithToken';
import CollectionModalContextProvider from 'modals/CollectionModal/CollectionModalContext';
import ErrorBoundaryProvider from 'contexts/ErrorBoundaryContext/ErrorBoundaryContext';
import UserProfileProvider from 'contexts/UserProfileContext/UserProfileContext';
import MilestonesProvider from 'controllers/business/Milestones/Milestones.controller';
import ApiFiltersProvider from 'contexts/ApiFiltersContext/ApiFiltersContext';
import GradientProvider from 'controllers/GradientController/GradientController';
import { I18nProvider } from 'contexts/I18nProvider/I18nProvider';
import { DeviceCodeAuthorizationModalProvider } from 'modals/DeviceCodeAuthorizationModal/DeviceCodeAuthorizationModalProvider';
import SubscriptionsAndAllowedFeaturesProvider from 'contexts/SubscriptionsAndAllowedFeaturesContext/SubscriptionsAndAllowedFeaturesContext';
import FreemiumGatingProvider from 'contexts/FreemiumGatingContext/FreemiumGatingContext';
import { AppSettingsProvider } from 'contexts/AppSettingsContext/AppSettingsContext';
import { ModalProviders } from 'modals/ModalProviders';
import FreeUserFreeDownloadUpsellModalProvider from 'modals/UpsellModals/FreeUserFreeDownloadUpsellModalContext';
import { lazyWithRetries } from 'utils/lazyWithRetries';
import { DeviceType } from '@landr/react-core';
import { LocalizedBrowserRouter } from 'contexts/LocalizedBrowserRouter/LocalizedBrowserRouter';
import AppStyleProvider from './AppGlobalStyle';

const App = lazyWithRetries(() => import('./App'));

interface ProviderContext {
    basename: string;
    deviceType: DeviceType;
    isExtendableHeight: boolean;
    isFooterAllowed: boolean;
}

const providers = [
    {
        component: ErrorBoundaryProvider,
        props: { key: 'ErrorBoundaryProvider' },
    },
    {
        component: ErrorBoundary,
        props: {
            variant: 'global',
            displayName: 'Root',
            key: 'ErrorBoundary',
        },
    },
    {
        component: Suspense,
        props: { fallback: null, key: 'Suspense' },
    },
    {
        component: ExecutionEnvironmentProvider,
        props: ({ deviceType }: ProviderContext) => ({
            key: 'ExecutionEnvironmentProvider',
            deviceType,
        }),
    },
    {
        component: AuthenticationProvider,
        props: { key: 'AuthenticationProvider' },
    },
    {
        component: AppSettingsProvider,
        props: ({ isExtendableHeight, isFooterAllowed }: ProviderContext) => ({
            key: 'AppSettingsProvider',
            isExtendableHeight,
            isFooterAllowed,
        }),
    },
    {
        component: I18nProvider,
        props: {
            key: 'I18nProvider',
        },
    },
    {
        component: LocalizedBrowserRouter,
        props: ({ basename }: ProviderContext) => ({
            key: 'LocalizedBrowserRouter',
            basename,
        }),
    },
    {
        component: DeviceCodeAuthorizationModalProvider,
        props: {
            key: 'DeviceCodeAuthorizationContextProvider',
        },
    },
    {
        component: PricingProviderWithToken,
        props: { key: 'PricingProviderWithToken' },
    },
    {
        component: SessionStorageProvider,
        props: { key: 'SessionStorageProvider' },
    },
    {
        component: ApolloProvider,
        props: { key: 'ApolloProvider' },
    },
    {
        component: AppStyleProvider,
        props: { key: 'AppStyleProvider' },
    },
    {
        component: MetadataRouter,
        props: { key: 'MetadataRouter' },
    },
    {
        component: NotificationsProvider,
        props: { key: 'NotificationsProvider' },
    },
    // Legacy - Use ModalProviders instead
    {
        component: ModalProvider,
        props: { key: 'ModalProvider' },
    },
    {
        component: FreeUserFreeDownloadUpsellModalProvider,
        props: { key: 'FreeUserFreeDownloadUpsellModalProvider' },
    },
    {
        component: SubscriptionsAndAllowedFeaturesProvider,
        props: { key: 'SubscriptionsAndAllowedFeaturesProvider' },
    },
    {
        component: FreemiumGatingProvider,
        props: { key: 'FreemiumGatingProvider' },
    },
    {
        component: UserProfileProvider,
        props: { key: 'UserProfileProvider' },
    },
    {
        component: MilestonesProvider,
        props: { key: 'MilestonesProvider' },
    },
    {
        component: UserCreditsProvider,
        props: { key: 'UserCreditsProvider' },
    },
    {
        component: CreatorSessionProvider,
        props: { key: 'CreatorSessionProvider' },
    },
    {
        component: ModalProviders,
        props: { key: 'ModalProviders' },
    },
    {
        component: PurchaseControllerProvider,
        props: { key: 'PurchaseControllerProvider' },
    },
    {
        component: ReactPlayerProvider,
        props: { key: 'ReactPlayerProvider' },
    },
    {
        component: ActivePlayerProvider,
        props: { key: 'ActivePlayerProvider' },
    },
    {
        component: SweetenerUpsellModalProvider,
        props: { key: 'SweetenerUpsellModalProvider' },
    },
    {
        component: SelectedSamplesProvider,
        props: { key: 'SelectedSamplesProvider' },
    },
    {
        component: OnboardingProvider,
        props: { key: 'OnboardingProvider' },
    },
    {
        component: CreatorContextProvider,
        props: { key: 'CreatorContextProvider' },
    },
    {
        component: ClientUpdateProvider,
        props: { key: 'ClientUpdateProvider' },
    },
    {
        component: CollectionModalContextProvider,
        props: { key: 'CollectionModalContextProvider' },
    },
    {
        component: ApiFiltersProvider,
        props: { key: 'ApiFiltersProvider' },
    },
    {
        component: GradientProvider,
        props: { key: 'GradientProvider' },
    },
];

export const RootProviders = ({
    basename,
    deviceType,
    isExtendableHeight,
    isFooterAllowed,
}: {
    basename: string;
    deviceType: DeviceType;
    isExtendableHeight: boolean;
    isFooterAllowed: boolean;
}) => {
    return providers.reduceRight(
        (acc, next) =>
            React.createElement(
                next.component as React.FC<{
                    key: string;
                }>,
                {
                    ...(typeof next.props === 'function'
                        ? next.props({
                              basename,
                              isExtendableHeight,
                              deviceType,
                              isFooterAllowed,
                          })
                        : next.props),
                },
                [acc],
            ),
        React.createElement(App, { key: 'App' }, null),
    );
};
